import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, MessageCircle, Phone, Mail, Instagram, Clapperboard, Clock3, Send, Loader2 } from "lucide-react";
import { CONTACT } from "@/constants/aero";
import { MountainVideo } from "@/components/aero/MountainVideo";
import { SOCIALS } from "@/constants/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

const PROJECT_TYPES = ["Feature Film", "OTT Series", "Commercial / Brand Film", "Wedding Film", "Real Estate", "Travel & Tourism", "Documentary", "Other"];
const BUDGETS = ["Under ₹50K", "₹50K — ₹1L", "₹1L — ₹3L", "₹3L+", "Not sure yet"];

const CONTACT_CARDS = [
  { icon: Phone, label: "Phone", value: "+91 7897862633", href: "tel:+917897862633" },
  { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
  { icon: Instagram, label: "Instagram", value: "@filmmakerakki", href: SOCIALS.instagram },
  { icon: Clapperboard, label: "IMDb", value: "Akhilesh Kumar Suman", href: CONTACT.imdb },
];

const inputCls =
  "w-full bg-[#F8F6F2] border border-[#E5E7EB] focus:border-[#4F6D8A] focus:shadow-[0_0_0_4px_rgba(79,109,138,0.12)] outline-none rounded-2xl px-5 py-4 text-[15px] text-[#111827] placeholder:text-[#9CA3AF] transition-all duration-300";

export const ContactSection = ({ onConsult }) => {
  const [waHref, setWaHref] = useState(SOCIALS.instagram);
  const [form, setForm] = useState({ name: "", phone: "", email: "", project_type: PROJECT_TYPES[0], date: "", budget: BUDGETS[4], brief: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/config`)
      .then((r) => {
        if (r.data.whatsapp_number) {
          setWaHref(`https://wa.me/${r.data.whatsapp_number}?text=${encodeURIComponent("Hi Akki! Project discuss karna hai.")}`);
        }
      })
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (form.name.trim().length < 2 || form.phone.trim().length < 10 || !/^\S+@\S+\.\S+$/.test(form.email) || form.brief.trim().length < 10) {
      toast.error("Naam, phone, email aur project details (min 10 chars) bharna zaroori hai");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/shoot-enquiries`, { ...form, name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), brief: form.brief.trim(), location: "" });
      toast.success("Inquiry bhej di gayi! 24 hours ke andar response milega.");
      setForm({ name: "", phone: "", email: "", project_type: PROJECT_TYPES[0], date: "", budget: BUDGETS[4], brief: "" });
    } catch {
      toast.error("Kuch gadbad ho gayi — WhatsApp par message kar do.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-white">
      {/* Contact hero banner */}
      <div className="relative overflow-hidden">
        <MountainVideo testid="contact-mountain-video" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#F8F6F2] via-[#F8F6F2]/75 to-transparent" />
        <div className="relative z-10 max-w-[1320px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease }}
            className="max-w-xl"
          >
            <p className="font-mono text-[14px] tracking-[0.3em] uppercase text-[#4F6D8A]">Let's Create Together</p>
            <h2 className="mt-5 font-serif text-[38px] md:text-[56px] leading-[1.05] text-[#111827]">
              Let's Create Your Next Cinematic Story
            </h2>
            <p className="mt-5 text-[#374151] leading-relaxed">
              Whether it's a feature film, brand campaign, wedding, documentary, or aerial production — let's turn your vision into unforgettable visuals.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a
                data-testid="contact-banner-book"
                href="/book-shoot"
                className="inline-flex items-center gap-2 bg-[#0F2A43] text-white font-mono text-[13px] font-bold tracking-[0.2em] uppercase px-8 py-4 rounded-full shadow-[0_15px_35px_-12px_rgba(15,42,67,0.5)] hover:bg-[#16395C] hover:-translate-y-0.5 transition-all duration-300"
              >
                Book a Shoot <ArrowUpRight size={13} />
              </a>
              <a
                data-testid="contact-banner-whatsapp"
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white border border-[#0F2A43]/25 text-[#0F2A43] font-mono text-[13px] font-bold tracking-[0.2em] uppercase px-8 py-4 rounded-full hover:bg-[#EEF2F6] hover:border-[#0F2A43] transition-all duration-300"
              >
                <MessageCircle size={13} /> WhatsApp Me
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Contact grid */}
      <div className="max-w-[1320px] mx-auto px-6 md:px-10 py-[100px] md:py-[120px]">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left — contact cards */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease }}
            className="lg:col-span-5"
          >
            <p className="font-mono text-[14px] tracking-[0.3em] uppercase text-[#4F6D8A]">Get In Touch</p>
            <h3 className="mt-4 font-serif text-4xl md:text-5xl text-[#111827]">Contact</h3>
            <span className="mt-5 block w-14 h-[2px] bg-[#4F6D8A]" />
            <div className="mt-10 space-y-4">
              {CONTACT_CARDS.map((c) => {
                const Icon = c.icon;
                return (
                  <a
                    key={c.label}
                    data-testid={`contact-card-${c.label.toLowerCase()}`}
                    href={c.href}
                    target={c.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="group flex items-center gap-5 bg-[#F8F6F2] border border-[#E5E7EB] rounded-[18px] px-6 py-5 hover:-translate-y-1 hover:shadow-[0_20px_45px_-18px_rgba(15,42,67,0.3)] hover:border-[#4F6D8A]/50 transition-all duration-300"
                  >
                    <span className="w-11 h-11 rounded-full bg-white border border-[#E5E7EB] flex items-center justify-center shrink-0 group-hover:border-[#4F6D8A]/50 transition-colors">
                      <Icon size={17} strokeWidth={1.4} className="text-[#6B7280] group-hover:text-[#0F2A43] transition-colors" />
                    </span>
                    <span>
                      <span className="block font-mono text-[12px] tracking-[0.22em] uppercase text-[#6B7280]">{c.label}</span>
                      <span className="block mt-1 font-medium text-[#111827] group-hover:text-[#0F2A43] transition-colors">{c.value}</span>
                    </span>
                  </a>
                );
              })}
            </div>
          </motion.div>

          {/* Right — booking form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1, ease }}
            className="lg:col-span-7 bg-white border border-[#E5E7EB] rounded-[22px] p-7 md:p-10 shadow-[0_25px_70px_-25px_rgba(15,42,67,0.25)]"
            data-testid="contact-form"
          >
            <p className="font-mono text-[14px] tracking-[0.3em] uppercase text-[#4F6D8A]">Booking Inquiry</p>
            <h3 className="mt-3 font-serif text-3xl md:text-4xl text-[#111827]">Tell Me About Your Project</h3>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <input data-testid="contact-form-name" value={form.name} onChange={set("name")} placeholder="Full Name" className={inputCls} />
              <input data-testid="contact-form-phone" value={form.phone} onChange={set("phone")} placeholder="Phone Number" className={inputCls} />
              <input data-testid="contact-form-email" type="email" value={form.email} onChange={set("email")} placeholder="Email" className={inputCls} />
              <select data-testid="contact-form-type" value={form.project_type} onChange={set("project_type")} className={`${inputCls} cursor-pointer`}>
                {PROJECT_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <input data-testid="contact-form-date" type="date" value={form.date} onChange={set("date")} min={new Date().toISOString().split("T")[0]} className={`${inputCls} cursor-pointer`} />
              <select data-testid="contact-form-budget" value={form.budget} onChange={set("budget")} className={`${inputCls} cursor-pointer`}>
                {BUDGETS.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <textarea
              data-testid="contact-form-brief"
              value={form.brief}
              onChange={set("brief")}
              placeholder="Tell me about your project — location, dates, vision..."
              rows={4}
              className={`${inputCls} mt-4 resize-none`}
            />
            <button
              data-testid="contact-form-submit"
              onClick={submit}
              disabled={sending}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#0F2A43] text-white font-mono text-[13px] font-bold tracking-[0.22em] uppercase px-8 py-4.5 rounded-full shadow-[0_15px_35px_-12px_rgba(15,42,67,0.5)] hover:bg-[#16395C] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 py-4"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={13} />} Send Inquiry
            </button>
            <p className="mt-5 text-center text-sm text-[#6B7280]">
              Ya seedha{" "}
              <button data-testid="contact-consult-link" onClick={onConsult} className="text-[#0F2A43] font-medium underline underline-offset-4 decoration-[#4F6D8A]/50 hover:decoration-[#0F2A43] transition-all">
                1:1 consult book karo — ₹299 / 30 min
              </button>
            </p>
          </motion.div>
        </div>

        {/* Quick response strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6, ease }}
          data-testid="contact-response-strip"
          className="mt-12 bg-[#F8F6F2] border border-[#E5E7EB] rounded-full px-8 py-5 flex items-center justify-center gap-3 text-center"
        >
          <Clock3 size={15} strokeWidth={1.5} className="text-[#4F6D8A] shrink-0" />
          <p className="text-[15px] text-[#374151]">Most inquiries receive a response within <span className="text-[#0F2A43] font-medium">24 hours.</span></p>
        </motion.div>
      </div>
    </section>
  );
};
